<script setup lang="ts">
/**
 * PdfPageViewer - Full-screen scrollable viewer for multi-page PDF documents.
 *
 * Fetches page images from the pdf-pages API endpoint and displays them
 * in a vertically scrollable container with page counter and download.
 */

import { Download, Loader2, RefreshCw, X, ZoomIn, ZoomOut } from '@lucide/vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { apiFetch } from '@ja/portal-query'

import { useCommunicationTranslations } from '../../composables/useCommunicationTranslations'
import type { CommunicationAttachment, PdfPagesResponse } from '../../types/communication'

const props = defineProps<{
  open: boolean
  attachment: CommunicationAttachment | null
  downloadLabel?: string
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'file-opened': [attachment: CommunicationAttachment]
  'file-closed': [attachment: CommunicationAttachment]
  'page-viewed': [attachment: CommunicationAttachment, page: number]
}>()

const { tm } = useCommunicationTranslations('communication')

// ============================================================================
// State
// ============================================================================

const pages = ref<PdfPagesResponse | null>(null)
const loading = ref(false)
const error = ref(false)
const currentPage = ref(1)
const zoom = ref(1)
const scrollContainerRef = ref<HTMLElement | null>(null)
const pageRefs = ref<HTMLElement[]>([])

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

const pageCountLabel = computed(() => {
  if (!pages.value) return ''
  return `${currentPage.value} / ${pages.value.page_count}`
})

// ============================================================================
// Data Fetching
// ============================================================================

async function fetchPages(): Promise<void> {
  if (!props.attachment?.pdf_pages_url) return

  loading.value = true
  error.value = false

  try {
    const data = await apiFetch<PdfPagesResponse>(props.attachment.pdf_pages_url)
    pages.value = data
  }
  catch {
    error.value = true
  }
  finally {
    loading.value = false
  }
}

// ============================================================================
// Intersection Observer for page tracking
// ============================================================================

let observer: IntersectionObserver | null = null

function setupObserver(): void {
  cleanupObserver()

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const pageNum = Number(entry.target.getAttribute('data-page'))
          if (pageNum && pageNum !== currentPage.value) {
            currentPage.value = pageNum
            if (props.attachment) {
              emit('page-viewed', props.attachment, pageNum)
            }
          }
        }
      }
    },
    {
      root: scrollContainerRef.value,
      threshold: 0.5,
    },
  )

  for (const el of pageRefs.value) {
    if (el) observer.observe(el)
  }
}

function cleanupObserver(): void {
  observer?.disconnect()
  observer = null
}

// ============================================================================
// Zoom
// ============================================================================

function zoomIn(): void {
  zoom.value = Math.min(ZOOM_MAX, zoom.value + ZOOM_STEP)
}

function zoomOut(): void {
  zoom.value = Math.max(ZOOM_MIN, zoom.value - ZOOM_STEP)
}

// ============================================================================
// Open / Close
// ============================================================================

function close(): void {
  if (props.attachment) {
    emit('file-closed', props.attachment)
  }
  emit('update:open', false)
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close()
}

watch(() => props.open, async (isOpen) => {
  if (isOpen && props.attachment?.pdf_pages_url) {
    currentPage.value = 1
    zoom.value = 1
    pages.value = null
    await fetchPages()

    if (props.attachment) {
      emit('file-opened', props.attachment)
    }

    document.addEventListener('keydown', onKeyDown)

    await nextTick()
    setupObserver()
  }
  else {
    cleanupObserver()
    document.removeEventListener('keydown', onKeyDown)
  }
})

onBeforeUnmount(() => {
  cleanupObserver()
  document.removeEventListener('keydown', onKeyDown)
})

function setPageRef(el: unknown, index: number): void {
  if (el instanceof HTMLElement) {
    pageRefs.value[index] = el
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex flex-col bg-black/95"
      >
        <!-- Header -->
        <div class="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div class="min-w-0 flex-1">
            <h2 class="truncate text-sm font-medium text-white">
              {{ attachment?.name }}
            </h2>
          </div>

          <div class="flex items-center gap-2">
            <!-- Page counter -->
            <span
              v-if="pages"
              class="whitespace-nowrap text-sm text-white/70"
            >
              {{ pageCountLabel }}
            </span>

            <!-- Zoom controls -->
            <div class="flex items-center gap-1">
              <button
                type="button"
                class="rounded p-1.5 text-white/70 hover:bg-white/20 hover:text-white disabled:opacity-40"
                :disabled="zoom <= ZOOM_MIN"
                @click="zoomOut"
              >
                <ZoomOut class="size-4" />
              </button>
              <span class="min-w-[3rem] text-center text-xs text-white/70">
                {{ Math.round(zoom * 100) }}%
              </span>
              <button
                type="button"
                class="rounded p-1.5 text-white/70 hover:bg-white/20 hover:text-white disabled:opacity-40"
                :disabled="zoom >= ZOOM_MAX"
                @click="zoomIn"
              >
                <ZoomIn class="size-4" />
              </button>
            </div>

            <!-- Download -->
            <a
              v-if="attachment?.url"
              :href="`${attachment.url}?download=1`"
              class="inline-flex items-center gap-1 rounded-md bg-white/10 px-2.5 py-1.5 text-sm text-white hover:bg-white/20"
            >
              <Download class="size-4" />
              {{ downloadLabel ?? tm('detail.download') }}
            </a>

            <!-- Close -->
            <button
              type="button"
              class="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              @click="close"
            >
              <X class="size-5" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div
          ref="scrollContainerRef"
          class="min-h-0 flex-1 overflow-y-auto overflow-x-auto"
        >
          <!-- Loading -->
          <div v-if="loading" class="flex h-full items-center justify-center">
            <div class="flex flex-col items-center gap-3">
              <Loader2 class="size-8 animate-spin text-white/60" />
              <span class="text-sm text-white/60">
                {{ tm('pdf_viewer.loading_pages') }}
              </span>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="flex h-full items-center justify-center">
            <div class="flex flex-col items-center gap-3">
              <p class="text-sm text-red-400">
                {{ tm('pdf_viewer.load_error') }}
              </p>
              <Button variant="secondary" size="sm" @click="fetchPages">
                <RefreshCw class="mr-2 size-4" />
                {{ tm('pdf_viewer.retry') }}
              </Button>
            </div>
          </div>

          <!-- Pages -->
          <div
            v-else-if="pages"
            class="mx-auto flex flex-col items-center gap-4 p-4"
            :style="{ width: `${100 * zoom}%`, maxWidth: `${1200 * zoom}px` }"
          >
            <div
              v-for="(page, index) in pages.pages"
              :key="page.page"
              :ref="(el) => setPageRef(el, index)"
              :data-page="page.page"
              class="w-full overflow-hidden rounded-lg border border-white/10 bg-white shadow-lg"
            >
              <img
                :src="page.url"
                :alt="`Page ${page.page}`"
                class="block w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
